import React from 'react';
import { Card, Table } from 'react-bootstrap';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { EmptyStateCardBody } from '../../../components/EmptyStateCard';
import { SkillLevelLabel } from '../../../pages/Skills/common/SkillLevelLabel';
import { TargetProfileGap, TargetProfileRow } from './logic';
import './targetProfile.css';

type Props = {
  rows: readonly TargetProfileRow[];
};

// The level badge keeps its own scale colour; the gap is shown as a ring around
// it so "how far off target" reads at a glance without losing the level colour.
const gapClassName: Record<TargetProfileGap, string> = {
  met: 'target-profile-gap-met',
  close: 'target-profile-gap-close',
  far: 'target-profile-gap-far',
  unknown: '',
};

export const TargetProfileCard: React.FC<React.PropsWithChildren<Props>> = ({
  rows,
}) => {
  const t = useNamespacedTranslation('skills.targetProfile');

  return (
    <Card>
      <Card.Header className="d-flex align-items-center gap-1">
        <span className="fw-bold">{t('header')}</span>
        <FlairIcon
          icon="information-circle-outline"
          size="sm"
          className="text-muted"
          tooltip={t('description')}
        />
      </Card.Header>
      {rows.length === 0 ? (
        <EmptyStateCardBody
          title={t('empty.title')}
          subtitle={t('empty.subtitle')}
        />
      ) : (
        <Card.Body>
          <Table responsive={true} className="mb-0">
            <thead>
              <tr>
                <th>{t('skillColumn')}</th>
                <th className="text-center">{t('currentLevelColumn')}</th>
                <th className="text-center">{t('requiredLevelColumn')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="align-middle">{row.skillName}</td>
                  <td>
                    <div className="d-flex justify-content-center">
                      <SkillLevelLabel
                        value={row.currentLevel}
                        className={gapClassName[row.gap]}
                        title={
                          row.gap === 'unknown' && row.currentLevel != null
                            ? undefined
                            : t(`gap.${row.gap}`)
                        }
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex justify-content-center">
                      <SkillLevelLabel value={row.targetLevel} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      )}
    </Card>
  );
};
